// ===========================================
// Integrations React Query Hooks
// Admin Third-Party Integrations Page
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as integrationsApi from '../api/integrations.api';
import type {
  ThirdPartyIntegrationSettingsInput,
  SetIntegrationKeyInput,
  VerifyIntegrationKeyInput,
  IntegrationProvider,
} from '../types/integrations.types';

// ===========================================
// QUERY KEYS
// ===========================================

export const integrationsKeys = {
  all: ['integrations'] as const,
  settings: () => [...integrationsKeys.all, 'settings'] as const,
  overview: () => [...integrationsKeys.all, 'overview'] as const,
  googleCalendarStats: () => [...integrationsKeys.all, 'google-calendar', 'stats'] as const,
  twilioStats: () => [...integrationsKeys.all, 'twilio', 'stats'] as const,
  whatsappStats: () => [...integrationsKeys.all, 'whatsapp', 'stats'] as const,
  integrationKeys: () => [...integrationsKeys.all, 'keys'] as const,
  integrationKeyStatus: () => [...integrationsKeys.all, 'key-status'] as const,
};

// ===========================================
// INTEGRATION SETTINGS
// ===========================================

/**
 * Get third-party integration settings
 */
export function useIntegrationSettings() {
  return useQuery({
    queryKey: integrationsKeys.settings(),
    queryFn: integrationsApi.getIntegrationSettings,
  });
}

/**
 * Update third-party integration settings
 */
export function useUpdateIntegrationSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: ThirdPartyIntegrationSettingsInput) =>
      integrationsApi.updateIntegrationSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      enqueueSnackbar('Integration settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update integration settings', {
        variant: 'error',
      });
    },
  });
}

/**
 * Reset integration settings to defaults
 */
export function useResetIntegrationSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: integrationsApi.resetIntegrationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      enqueueSnackbar('Integration settings reset to defaults', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to reset integration settings', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// INTEGRATION OVERVIEW
// ===========================================

/**
 * Get aggregated overview of all third-party integrations
 */
export function useIntegrationOverview() {
  return useQuery({
    queryKey: integrationsKeys.overview(),
    queryFn: integrationsApi.getIntegrationOverview,
    // Refresh every 30 seconds to keep stats up to date
    refetchInterval: 30000,
  });
}

// ===========================================
// GOOGLE CALENDAR STATS
// ===========================================

/**
 * Get detailed Google Calendar integration statistics
 */
export function useGoogleCalendarStats() {
  return useQuery({
    queryKey: integrationsKeys.googleCalendarStats(),
    queryFn: integrationsApi.getGoogleCalendarStats,
    // Refresh every 30 seconds
    refetchInterval: 30000,
  });
}

// ===========================================
// TWILIO/SMS STATS
// ===========================================

/**
 * Get detailed Twilio SMS statistics
 */
export function useTwilioStats() {
  return useQuery({
    queryKey: integrationsKeys.twilioStats(),
    queryFn: integrationsApi.getTwilioStats,
    // Refresh every 30 seconds
    refetchInterval: 30000,
  });
}

// ===========================================
// WHATSAPP STATS (Meta Cloud API)
// ===========================================

/**
 * Get detailed WhatsApp messaging statistics
 */
export function useWhatsAppStats() {
  return useQuery({
    queryKey: integrationsKeys.whatsappStats(),
    queryFn: integrationsApi.getWhatsAppStats,
    // Refresh every 30 seconds
    refetchInterval: 30000,
  });
}

// ===========================================
// INTEGRATION KEYS
// ===========================================

/**
 * Get per-integration key status summaries
 */
export function useIntegrationKeyStatus() {
  return useQuery({
    queryKey: integrationsKeys.integrationKeyStatus(),
    queryFn: integrationsApi.getIntegrationKeyStatus,
  });
}

/**
 * Set/update an integration key
 */
export function useSetIntegrationKey() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: SetIntegrationKeyInput) =>
      integrationsApi.setIntegrationKey(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      enqueueSnackbar(`${variables.integration} key saved`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to save integration key', {
        variant: 'error',
      });
    },
  });
}

/**
 * Delete an integration key
 */
export function useDeleteIntegrationKey() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ integration, keyName }: { integration: IntegrationProvider; keyName: string }) =>
      integrationsApi.deleteIntegrationKey(integration, keyName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      enqueueSnackbar('Integration key deleted', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to delete integration key', {
        variant: 'error',
      });
    },
  });
}

/**
 * Verify a stored integration key
 */
export function useVerifyIntegrationKey() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: VerifyIntegrationKeyInput) =>
      integrationsApi.verifyIntegrationKey(data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      if (data.valid) {
        enqueueSnackbar(`${variables.integration} key verified successfully`, { variant: 'success' });
      } else {
        enqueueSnackbar(`${variables.integration} key verification failed: ${data.error}`, { variant: 'warning' });
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to verify integration key', {
        variant: 'error',
      });
    },
  });
}
